import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { PriceDTO } from "@shared/models/price-dto";
import { environment } from "@env";
import {
  ManagePartDTO,
  PartDerivationSaveDTO,
  PartDerivationSegments,
  PartDriveBDTO,
  PartDTO
} from "@shared/models/part-dto";
import { LanguageService } from "./language.service";
import { MeasurementUnitDTO } from "@shared/models/measurement-unit-dto";
import { PageManager } from "@shared/models/page-manager";
import { Pagination } from "@shared/models/pagination";
import { PartSearchFilterDto } from "@shared/models/part-search-filter-dto";
import { DateHelperService } from "./date.helper.service";
import { InterFieldsNameDTO } from "@shared/models/inter-fields-names-dto";
import { SynonymDTO } from "@shared/models/synonym-dto";
import { RequestApproval } from "@shared/models/registration-request";

@Injectable({
  providedIn: "root"
})
export class PartService {
  private partUrl = "parts/";
  private interFieldNameUrl = "inter-field-names/";
  private synonymsUrl = "synonyms/";
  private language: string;

  constructor(
    private http: HttpClient,
    private languageService: LanguageService,
    private dateHelperService: DateHelperService
  ) {
    this.languageService.getLanguage$().subscribe((res) => {
      this.language = res;
    });
  }

  listAll() {
    return this.languageService.getLanguage$().pipe(
      map((currentLanguage) => {
        const headers = new HttpHeaders({
          "Content-Type": "application/json",
          "Accept-Language": currentLanguage
        });
        return this.http
          .get<PartDTO[]>(environment.apiUrl + this.partUrl, { headers })
          .pipe(map((service) => service));
      })
    );
  }

  findById(id: number): Observable<PartDTO> {
    return this.http.get<PartDTO>(environment.apiUrl + `${this.partUrl}${id}`);
  }

  /**
   * @returns A paginated array with the parts that match the given filters
   *
   * @deprecated This uses a deprecated db.
   */
  findByFiltersDeprecatedAPI(
    searchFormDTO: any,
    pageManager: PageManager,
    sort = ""
  ) {
    let query = "";
    if (searchFormDTO.manufacturerId) {
      query += `&manufacturerId=${searchFormDTO.manufacturerId}`;
    }
    if (searchFormDTO.description) {
      query += `&description=${searchFormDTO.description}`;
    }
    if (searchFormDTO.descriptionFilter) {
      query += `&descriptionFilter=${searchFormDTO.descriptionFilter}`;
    }
    if (searchFormDTO.categoryId) {
      query += `&categoryId=${searchFormDTO.categoryId}`;
    }
    if (searchFormDTO.partCode) {
      query += `&partCode=${searchFormDTO.partCode}`;
    }

    return this.languageService.getLanguage$().pipe(
      map((currentLanguage) => {
        const headers = new HttpHeaders({
          "Content-Type": "application/json",
          "Accept-Language": currentLanguage
        });
        return this.http.get<Pagination<PartDTO[]>>(
          environment.apiUrl +
            `${this.partUrl}filters?page=${pageManager.pageIndex}&size=${pageManager.pageSize}${query}${sort}`,
          { headers }
        );
      })
    );
  }

  findByManufactureOrPartNameOrPartCodeAPI(
    searchFormDTO: PartSearchFilterDto,
    pageManager: PageManager,
    sort = "",
    clientId?: string,
    vehicleId?: string,
    fee?: boolean
  ) {
    return this.languageService.getLanguage$().pipe(
      switchMap((currentLanguage) => {
        const headers = new HttpHeaders({
          "Content-Type": "application/json",
          "Accept-Language": currentLanguage
        });

        let query = `?value=${searchFormDTO.value}&page=${pageManager.pageIndex}&size=${pageManager.pageSize}`;

        if (clientId) {
          query += `&clientId=${clientId}`;
        }

        if (vehicleId) {
          query += `&vehicleId=${vehicleId}`;
        }

        if (fee) {
          query += `&fee=${fee}`;
        }

        if (sort) {
          query += `&sort=${sort}`;
        }

        return this.http.get<PartDTO[]>(
          environment.apiUrl + this.partUrl + "search" + query,
          { headers }
        );
      })
    );
  }

  findAllPricesByPartId(priceId: number): Observable<PriceDTO[]> {
    return this.http.get<PriceDTO[]>(
      environment.apiUrl + `${this.partUrl}${priceId}/prices`
    );
  }

  findByIdDefaultPartPrice(
    id: number,
    codeCurrency: string
  ): Observable<PriceDTO> {
    return this.http.get<PriceDTO>(
      environment.apiUrl +
        `${this.partUrl}${id}/default-price?currencyCode=${codeCurrency}`
    );
  }

  findByRegion(
    id: number,
    carWorkshopId: number,
    serviceRequestId: number
  ): Observable<any> {
    let params = new HttpParams();
    params = params.set("serviceRequestId", serviceRequestId.toString());
    return this.http.get(
      `${environment.apiUrl}${this.partUrl}${id}/car-workshops/${carWorkshopId}/prices`,
      { params }
    );
  }

  getMeasurementUnits(lang: string): MeasurementUnitDTO[] {
    const options: MeasurementUnitDTO[] = [];
    const translateService = this.languageService.getTranslateService();

    options.push({
      value: "JG",
      description: translateService.instant("measurement-units.jg")
    });
    options.push({
      value: "CJT",
      description: translateService.instant("measurement-units.cjt")
    });
    options.push({
      value: "L",
      description: translateService.instant("measurement-units.l")
    });
    options.push({
      value: "PC",
      description: translateService.instant("measurement-units.pc")
    });
    options.push({
      value: "UN",
      description: translateService.instant("measurement-units.un")
    });

    return options;
  }

  validatePart(queryParams: {
    portugueseName: string;
    englishName: string;
    spanishName: string;
  }): Observable<void> {
    return this.http.get<void>(
      `${environment.microserviceParts}${this.partUrl}alreadyExists?englishName=${queryParams.englishName}&portugueseName=${queryParams.portugueseName}&spanishName=${queryParams.spanishName}`
    );
  }

  save(partDTO: PartDTO): Observable<any> {
    return this.http.post<any>(
      environment.microserviceParts + "parts",
      partDTO
    );
  }

  listAllNames(): Observable<InterFieldsNameDTO[]> {
    return this.http.get<InterFieldsNameDTO[]>(
      environment.apiUrl + this.partUrl + `${this.interFieldNameUrl}`
    );
  }

  delete(id: number): Observable<any> {
    return this.http.delete<any>(
      environment.microserviceParts + `${this.partUrl}${id}`
    );
  }

  findByIdDriveB(id: number): Observable<PartDriveBDTO> {
    return this.http.get<PartDriveBDTO>(
      `${environment.microserviceParts}${this.partUrl}${id}`
    );
  }

  getSegments(): Observable<PartDerivationSegments[]> {
    return this.http.get<PartDerivationSegments[]>(
      `${environment.microserviceParts}segments`
    );
  }

  partCodeAlreadyExists(partCode: string): Observable<boolean> {
    return this.http.get<boolean>(
      `${environment.microserviceParts}part-derivations/alreadyExists?partCode=${partCode}`
    );
  }

  saveDerivation(
    partDerivationSaveDTO: PartDerivationSaveDTO
  ): Observable<any> {
    return this.http.post<any>(
      `${environment.microserviceParts}part-derivations/request-approval`,
      partDerivationSaveDTO
    );
  }

  findByFilters(searchFormDTO: any, pageManager: PageManager, sort = "") {
    let query = "";
    if (searchFormDTO.description) {
      query += `&description=${searchFormDTO.description}`;
    }
    if (searchFormDTO.descriptionFilter) {
      query += `&descriptionFilter=${searchFormDTO.descriptionFilter}`;
    }
    if (searchFormDTO.categoryId) {
      query += `&partCategoryId=${searchFormDTO.categoryId}`;
    }

    return this.languageService.getLanguage$().pipe(
      map((currentLanguage) => {
        const headers = new HttpHeaders({
          "Content-Type": "application/json",
          "Accept-Language": currentLanguage
        });
        return this.http.get<Pagination<ManagePartDTO[]>>(
          environment.microserviceParts +
            `parts?size=${pageManager.pageSize}&page=${pageManager.pageIndex}${query}${sort}`,
          { headers }
        );
      })
    );
  }

  findPartsSimplified(searchFormDTO: any): Observable<any> {
    let query = "";
    if (searchFormDTO.description) {
      query += `?description=${searchFormDTO.description}`;
    }

    return this.http.get<any[]>(
      environment.microserviceParts + `parts${query}`
    );
  }

  edit(id: number, partDTO: PartDTO): Observable<any> {
    return this.http.put<any>(
      environment.apiUrl + `${this.partUrl}${id}`,
      partDTO
    );
  }

  saveSynonym(id: number, synonymDTO: SynonymDTO): Observable<any> {
    return this.http.post<any>(
      environment.apiUrl + `${this.partUrl}${id}/${this.synonymsUrl}`,
      synonymDTO
    );
  }

  deleteSynonym(id: number, synonymId: number): Observable<any> {
    return this.http.delete<any>(
      environment.apiUrl +
        `${this.partUrl}${id}/${this.synonymsUrl}${synonymId}`
    );
  }

  findByIdSynonym(id: number): Observable<any> {
    return this.http.get<any>(
      environment.apiUrl + `${this.partUrl}${id}/${this.synonymsUrl}`
    );
  }

  updatePartPrice(priceId: number, priceDTO: PriceDTO): Observable<any> {
    return this.http.put<any>(
      environment.apiUrl + `${this.partUrl}${priceId}/prices`,
      priceDTO
    );
  }

  getPriceDerivationApprovalHistory(partDerivationId: number) {
    return this.http.get<any[]>(
      `${environment.microserviceParts}prices/request-approval/history/${partDerivationId}`
    );
  }

  getMeasurementUnitDescription(lang: string, value: string) {
    const measurementUnits = this.getMeasurementUnits(lang);
    return measurementUnits.filter((item) => item.value === value)[0]
      .description;
  }

  getPartsApprovalQueue(
    searchFormDTO: any,
    pageManager: PageManager,
    sort: string
  ): Observable<any> {
    let query = "";
    if (searchFormDTO.createdAt) {
      query += `&createdAt=${this.dateHelperService.convertToDefaultFormat(
        searchFormDTO.createdAt
      )}`;
    }
    if (searchFormDTO.description) {
      query += `&description=${searchFormDTO.description}`;
    }
    if (searchFormDTO.createdBy) {
      query += `&createdBy=${searchFormDTO.createdBy}`;
    }
    return this.http.get<any>(
      environment.microserviceParts +
        `${this.partUrl}request-approval?page=${pageManager.pageIndex}&size=${pageManager.pageSize}${query}${sort}`
    );
  }

  getAllRequestApproval(
    searchFormDTO: any,
    pageManager: PageManager,
    sort: string
  ) {
    let query = "";
    if (searchFormDTO.createdAt) {
      query += `&createdAt=${this.dateHelperService.convertToDefaultFormat(
        searchFormDTO.createdAt
      )}`;
    }

    if (searchFormDTO.createdBy) {
      query += `&createdBy=${searchFormDTO.createdBy}`;
    }

    switch (this.language) {
      case "en": {
        sort = sort.replace("description", "descriptionEn");
        if (searchFormDTO.description) {
          query += `&descriptionEn=${searchFormDTO.description}`;
        }
        break;
      }
      case "es": {
        sort = sort.replace("description", "descriptionEs");
        if (searchFormDTO.description) {
          query += `&descriptionEs=${searchFormDTO.description}`;
        }
        break;
      }
      default:
        sort = sort.replace("description", "descriptionPt");
        if (searchFormDTO.description) {
          query += `&descriptionPt=${searchFormDTO.description}`;
        }
        break;
    }

    if (searchFormDTO.requestType) {
      query += `&requestType=${searchFormDTO.requestType}`;
    }

    return this.http.get<Pagination<RequestApproval[]>>(
      `${environment.microserviceParts}request-approval/get-all?page=${pageManager.pageIndex}&size=${pageManager.pageSize}${query}${sort}`
    );
  }

  getDerivationById(entityId) {
    return this.http.get<Observable<any>>(
      `${environment.microserviceParts}part-derivations/derivation/${entityId}`
    );
  }

  getPriceById(requestId) {
    return this.http.get<Observable<any>>(
      `${environment.microserviceParts}prices/request-approval/${requestId}`
    );
  }
}
